import type {
  GiftCardRestriction,
  GiftCardStatus,
  IssueGiftCardRequest,
  IssuedGiftCardDetail,
  IssuedGiftCardFilters,
  IssuedGiftCardHolderStats,
  IssuedGiftCardSummary,
} from "@/types/billing/issued-gift-card";
import type { Page } from "@/types/pagination";
import type { RainforestPayinConfig } from "@/types/rainforest";

import { requestData } from "@/api/api-config";

export interface GiftCardBalanceResponse {
  code: string;
  currentBalance: number;
  initialValue: number;
  isActive: boolean;
  restriction: GiftCardRestriction | null;
}

export interface GiftCardCodeValidationResponse {
  code: string;
  available: boolean;
}

export interface GiftCardCodeSearchResult {
  id: string;
  code: string;
  currentBalance: number;
  status: GiftCardStatus;
  purchasingPatientId: string | null;
  purchasingPatientName: string | null;
  restriction: GiftCardRestriction | null;
}

/**
 * Search issued gift cards by (partial) code for the command palette.
 * POST keeps the code out of access logs/proxies/history. `catchError` is
 * disabled so a denied/empty lookup never toasts — it's a best-effort, lowest
 * priority addition to the global search.
 */
export async function searchGiftCardsByCode(
  query: string,
  signal?: AbortSignal,
): Promise<GiftCardCodeSearchResult[] | null> {
  return await requestData<GiftCardCodeSearchResult[]>(
    `billing/gift-cards/search`,
    "post",
    {
      json: { query },
      ...(signal && { signal }),
    },
    false,
  );
}

export async function getGiftCardBalance(
  code: string,
): Promise<GiftCardBalanceResponse | null> {
  return await requestData<GiftCardBalanceResponse>(
    `billing/gift-cards/balance`,
    "post",
    {
      json: { gift_card_code: code },
    },
  );
}

export async function validateGiftCardCode(
  code: string,
  options?: { chargeItemId?: string },
): Promise<GiftCardCodeValidationResponse | null> {
  return await requestData<GiftCardCodeValidationResponse>(
    `billing/gift-cards/code/validate`,
    "post",
    {
      json: {
        code,
        ...(options?.chargeItemId
          ? { charge_item_id: options.chargeItemId }
          : {}),
      },
    },
    false,
  );
}

export async function getIssuedGiftCardHolderStats(
  holderPatientId: string,
): Promise<IssuedGiftCardHolderStats | null> {
  return await requestData<IssuedGiftCardHolderStats>(
    `billing/gift-cards/issued/holder-stats`,
    "get",
    {
      searchParams: {
        holder_patient_id: holderPatientId,
      },
    },
  );
}

export async function listIssuedGiftCards(
  filters: IssuedGiftCardFilters,
): Promise<Page<IssuedGiftCardSummary> | null> {
  return await requestData<Page<IssuedGiftCardSummary>>(
    `billing/gift-cards/issued`,
    "get",
    {
      searchParams: {
        page: filters.page.toString(),
        per_page: filters.perPage.toString(),
        sort_by: filters.sortBy,
        sort_direction: filters.sortDirection,
        ...(filters.status ? { status: filters.status } : {}),
        ...(filters.purchaser ? { purchaser: filters.purchaser } : {}),
        ...(filters.recipient ? { recipient: filters.recipient } : {}),
        ...(filters.patientId ? { patient_id: filters.patientId } : {}),
        ...(filters.holderPatientId
          ? { holder_patient_id: filters.holderPatientId }
          : {}),
        ...(filters.dateIssued?.[0]
          ? { date_from: filters.dateIssued[0] }
          : {}),
        ...(filters.dateIssued?.[1] ? { date_to: filters.dateIssued[1] } : {}),
      },
    },
  );
}

export async function getIssuedGiftCardDetail(
  id: string,
): Promise<IssuedGiftCardDetail | null> {
  return await requestData<IssuedGiftCardDetail>(
    `billing/gift-cards/issued/${id}`,
    "get",
  );
}

export async function updateIssuedGiftCardRecipient(
  giftCardId: string,
  data: {
    recipientPatientId?: string | null;
    recipientNote?: string | null;
  },
): Promise<IssuedGiftCardDetail | null> {
  return await requestData<IssuedGiftCardDetail>(
    `billing/gift-cards/issued/${giftCardId}`,
    "patch",
    { json: data },
  );
}

export async function sendGiftCardToRecipient(
  giftCardId: string,
): Promise<unknown> {
  return await requestData(
    `billing/gift-cards/issued/${giftCardId}/send`,
    "post",
  );
}

export async function sendGiftCardToPurchaser(
  giftCardId: string,
): Promise<unknown> {
  return await requestData(
    `billing/gift-cards/issued/${giftCardId}/send-to-purchaser`,
    "post",
  );
}

export async function voidIssuedGiftCard(
  giftCardId: string,
  reason: string,
): Promise<IssuedGiftCardDetail | null> {
  return await requestData<IssuedGiftCardDetail>(
    `billing/gift-cards/issued/${giftCardId}/void`,
    "post",
    { json: { reason } },
    false,
  );
}

export async function startGiftCardIssuance(
  data: IssueGiftCardRequest,
): Promise<RainforestPayinConfig | null> {
  return await requestData<RainforestPayinConfig>(
    `billing/gift-cards/issue`,
    "post",
    { json: data },
  );
}

export async function activateIssuedGiftCard(
  giftCardId: string,
  sendNotification: boolean,
): Promise<IssuedGiftCardDetail | null> {
  // catchError=false so a failed activation rejects (the drawer shows an error
  // toast) instead of resolving null and tripping the success path.
  return await requestData<IssuedGiftCardDetail>(
    `billing/gift-cards/issued/${giftCardId}/activate`,
    "post",
    { json: { send_notification: sendNotification } },
    false,
  );
}
